import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { PasswordInput } from "../components/PasswordInput";
import { Smartphone, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import logoNavy from "../assets/logo-navy.png";

export default function Login() {
  const { login, loginWithOtpResult } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/account";
  const [mode, setMode] = useState("otp"); // otp | password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // otp state
  const [phone, setPhone] = useState("");
  const [otpStep, setOtpStep] = useState(1);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [devOtp, setDevOtp] = useState(null);

  const submitPassword = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(email, password); toast.success("Welcome back!"); navigate(next); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const requestOtp = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone });
      setDevOtp(data.dev_otp || null);
      setOtpStep(2);
      toast.success(data.dev_otp ? "OTP generated (test mode)" : "OTP sent to your mobile");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, code, name });
      loginWithOtpResult(data);
      toast.success("Logged in!");
      navigate(next);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-black/5 p-8 shadow-sm">
        <div className="text-center mb-6">
          <img src={logoNavy} alt="Begum's Biryani Secret" className="mx-auto h-16 w-auto mb-3" />
          <h1 className="font-serif text-3xl text-navy">Welcome Back</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your royal account</p>
        </div>

        <div className="flex bg-beige rounded-full p-1 mb-6">
          <button onClick={() => { setMode("otp"); setError(""); }} data-testid="mode-otp" className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-medium transition-colors ${mode === "otp" ? "bg-navy text-cream" : "text-navy"}`}><Smartphone size={15} /> Login with OTP</button>
          <button onClick={() => { setMode("password"); setError(""); }} data-testid="mode-password" className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-medium transition-colors ${mode === "password" ? "bg-navy text-cream" : "text-navy"}`}><Mail size={15} /> Login with Email</button>
        </div>

        {mode === "password" ? (
          <form onSubmit={submitPassword} className="space-y-4">
            <div><label className="text-xs font-medium text-muted-foreground">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="w-full mt-1 border border-black/15 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" /></div>
            <div><label className="text-xs font-medium text-muted-foreground">Password</label><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required testId="login-password" className="w-full mt-1 border border-black/15 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" /></div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={loading} data-testid="login-submit" className="w-full btn-shine bg-navy text-cream font-semibold py-3 rounded-full hover:bg-navy-soft transition-colors disabled:opacity-60">{loading ? "Signing in..." : "Sign In"}</button>
          </form>
        ) : otpStep === 1 ? (
          <form onSubmit={requestOtp} className="space-y-4">
            <div><label className="text-xs font-medium text-muted-foreground">Mobile Number</label>
              <div className="flex items-center mt-1 border border-black/15 rounded-lg px-3.5 focus-within:ring-2 focus-within:ring-gold">
                <span className="text-sm text-muted-foreground">+91</span>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="98765 43210" data-testid="otp-phone" className="w-full py-2.5 pl-2 text-sm outline-none" />
              </div>
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={loading} data-testid="otp-request-btn" className="w-full btn-shine bg-navy text-cream font-semibold py-3 rounded-full hover:bg-navy-soft transition-colors disabled:opacity-60">{loading ? "Sending..." : "Send OTP"}</button>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="space-y-4">
            <button type="button" onClick={() => { setOtpStep(1); setCode(""); setError(""); }} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"><ArrowLeft size={13} /> Change number</button>
            {devOtp && <div className="text-xs bg-gold/15 text-navy rounded-lg px-3 py-2" data-testid="dev-otp-hint">Test mode: your code is <b>{devOtp}</b></div>}
            <div><label className="text-xs font-medium text-muted-foreground">Enter OTP sent to +91 {phone}</label><input value={code} onChange={(e) => setCode(e.target.value)} required maxLength={6} placeholder="6-digit code" data-testid="otp-code" className="w-full mt-1 border border-black/15 rounded-lg px-3.5 py-2.5 text-sm tracking-[0.4em] text-center outline-none focus:ring-2 focus:ring-gold" /></div>
            <div><label className="text-xs font-medium text-muted-foreground">Your Name (new users)</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" data-testid="otp-name" className="w-full mt-1 border border-black/15 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" /></div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={loading} data-testid="otp-verify-btn" className="w-full btn-shine bg-gold text-navy font-semibold py-3 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">{loading ? "Verifying..." : "Verify & Continue"}</button>
          </form>
        )}

        <div className="text-center text-sm text-muted-foreground mt-5">New here? <Link to="/register" className="text-navy font-semibold hover:text-gold">Create account</Link></div>
        <div className="text-center mt-3"><Link to="/menu" className="text-xs text-muted-foreground hover:text-navy">Continue as guest →</Link></div>
      </div>
    </div>
  );
}
